import { FC, useContext, useEffect, useRef, useState, useMemo } from 'react'
import jsLogger from 'js-logger'

import { dbGetLocalization, getRecapInfo } from 'controllers/main'
import PageContainer from 'shared/components/PageContainer'
import Loading from 'components/Loading'
import { TwoFactorT } from 'types/twoFactor'
import PACKAGE from '../../package.json'
import LocalizationContext from 'shared/contexts/LocalizationContext'
import VideoAnalysis from 'pages/recap/VideoAnalysis'
import {
  createSessionTranscript,
  getDemo2Screenshot,
  getSessionDuration
} from 'src/utils'
import SessionSummaryCard from 'pages/recap/SessionSummaryCard'
import { useSmartScroll } from 'pages/recap/useSmartScroll'
import AISummary from 'pages/recap/AISummary'
import SessionPlayback from 'pages/recap/SessionPlayback'
import TranscriptSection from 'pages/recap/TranscriptSection'
import RecognitionSection from 'pages/recap/RecognitionSection'

type Props = {
  interactionId: string
  isTest: boolean
  locale: string
  animationOn: boolean
}

const Recap: FC<Props> = ({
  interactionId,
  isTest,
  locale: deviceLocale,
  animationOn
}) => {
  const { getString, setLocalization } = useContext(LocalizationContext)
  const [writingDone, setWritingDone] = useState(!animationOn)
  const [data, setData] = useState<TwoFactorT.RecapT | null>(null)
  const updateTimerRef = useRef<number>()
  const requestsAmountRef = useRef(0)

  const locale = data?.lang || deviceLocale

  const {
    isUserScrolledUp,
    hasNewContentBelow,
    scrollToBottom,
    forceScrollToBottom,
    handleScroll,
    scrollContainerRef,
    contentRef
  } = useSmartScroll()

  useEffect(() => {
    const run = async () => {
      const localization = await dbGetLocalization()
      if (localization) {
        setLocalization(localization)
      }
    }
    run()
  }, [])

  useEffect(() => {
    jsLogger.log('RECAP page', { interactionId })
    const fetchData = async () => {
      const res = await getRecapInfo(interactionId)
      requestsAmountRef.current = requestsAmountRef.current + 1
      const screenshot = res ? getDemo2Screenshot(res) : undefined
      if (
        requestsAmountRef.current > 60 ||
        (res &&
          screenshot &&
          screenshot.aiAnalysis &&
          res.videoAIAnalysis &&
          res.avatarVideoUrl)
      ) {
        clearInterval(updateTimerRef.current)
      }
      setData(res)
    }

    updateTimerRef.current = window.setInterval(fetchData, 5000)
    fetchData()

    return () => clearInterval(updateTimerRef.current)
  }, [interactionId])

  const duration = useMemo(() => {
    if (data) {
      return getSessionDuration(data) / 1000
    }
  }, [data])

  const screenshot = data ? getDemo2Screenshot(data) : undefined
  const transcript = data ? createSessionTranscript(data) : undefined
  const aiAnalysis = screenshot?.aiAnalysis
  const primaryVideoUrl = data?.avatarVideoUrl
  const videoPoster = screenshot?.url
  // const livenessAnalysys = find(
  //   aiAnalysis?.analysis,
  //   (a) => a.title === 'Real or Virtual'
  // )

  const renderInfo = () => {
    if (data) {
      // const uInfo = get(data, 'userInfo') as Record<string, any> | undefined
      return (
        <div className='flex flex-col flex-1 overflow-y-auto no-scrollbar w-full pb-8'>
          <SessionSummaryCard report={data} screenshots={screenshot} />

          {aiAnalysis?.overallSummary && (
            <AISummary
              locale={locale}
              scrollToBottom={scrollToBottom}
              overallSummary={aiAnalysis.overallSummary}
              onWritingDone={() => setWritingDone(true)}
              animationOn={animationOn}
            />
          )}

          {writingDone && data && data.user && (
            <RecognitionSection user={data.user} />
          )}

          {/* {writingDone && livenessAnalysys && aiAnalysis && (
            <LivenessAnalysis
              locale={locale}
              analysis={livenessAnalysys}
              realPersonOrVirtual={aiAnalysis.realPersonOrVirtual}
            />
          )} */}

          {/* <div className='px-4 w-full max-w-xl mx-auto animate-fadeIn'>
            <AnalysisContent
              overallSummary={aiAnalysis?.overallSummary}
              analysis={aiAnalysis?.analysis}
              steps={data.steps}
              userInfo={get(data, 'userInfo')}
              locale={locale}
              onWritingDone={() => setWritingDone(true)}
            />
          </div> */}

          {writingDone && (
            <VideoAnalysis
              videoAIAnalysis={data.videoAIAnalysis}
              user={data.user}
              sessionId={interactionId}
            />
          )}
          {writingDone && (
            <SessionPlayback
              primaryVideoUrl={primaryVideoUrl}
              videoPoster={videoPoster}
            />
          )}
          {writingDone && transcript && (
            <TranscriptSection transcript={transcript} language={data.lang} />
          )}
        </div>
      )
    }
  }

  const sessionDate = data?.createdAt
  const sessionDateConf: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }
  const formattedDate = sessionDate
    ? new Date(sessionDate).toLocaleDateString('en-US', sessionDateConf)
    : null

  const renderHeader = () => {
    return (
      <div className='mt-8 mb-6 flex flex-row justify-between items-start gap-4'>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-3 flex-wrap'>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>
              {getString('recap_header', locale)}
            </h1>
          </div>
          {formattedDate && (
            <p className='text-sm text-gray-500 mt-1'>
              {formattedDate}
              {duration && duration > 0 && (
                <span>
                  {' '}
                  • {Math.floor(duration / 60)}m {Math.floor(duration % 60)}s
                </span>
              )}
            </p>
          )}
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <PageContainer
        version={PACKAGE.version}
        contentClassName='h-[100dvh] bg-gray-50 pt-[env(safe-area-inset-top)] w-full max-w-4xl'
      >
        <Loading waitingForUpload={false} />
      </PageContainer>
    )
  } else {
    return (
      <PageContainer
        version={PACKAGE.version}
        contentClassName='h-[100dvh] bg-gray-50 pt-[env(safe-area-inset-top)]  w-full max-w-4xl'
      >
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className='h-[calc(100dvh-env(safe-area-inset-top))] overflow-y-auto overflow-x-hidden px-4'
        >
          <div ref={contentRef} className='w-full'>
            {renderHeader()}
            {renderInfo()}
          </div>
        </div>
      </PageContainer>
    )
  }
}

export default Recap
